/* ═══════════════════════════════════════════════════════
   MIZU LUXE — main.js
   ═══════════════════════════════════════════════════════ */

'use strict';

/* ── Navigation scroll ─────────────────────────────── */
const nav = document.getElementById('nav');

function handleNavScroll() {
  if (nav) nav.classList.toggle('scrolled', window.scrollY > 60);
}

window.addEventListener('scroll', handleNavScroll, { passive: true });


/* ── Mobile menu ────────────────────────────────────── */
const burger = document.getElementById('burger');
const mobileMenu = document.getElementById('mobileMenu');

function setMobileMenu(open) {
  mobileMenu.classList.toggle('open', open);
  document.body.style.overflow = open ? 'hidden' : '';
  burger.setAttribute('aria-expanded', open ? 'true' : 'false');
}

if (burger && mobileMenu) {
  burger.setAttribute('aria-expanded', 'false');
  burger.setAttribute('aria-controls', 'mobileMenu');

  burger.addEventListener('click', () => {
    setMobileMenu(!mobileMenu.classList.contains('open'));
  });

  mobileMenu.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => setMobileMenu(false));
  });

  // Échap ferme le menu et rend le focus au bouton (accessibilité clavier)
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && mobileMenu.classList.contains('open')) {
      setMobileMenu(false);
      burger.focus();
    }
  });
}


/* ── Scroll reveal ──────────────────────────────────── */
function initReveal() {
  // Auto-anime le contenu éditorial des pages (légales, FAQ, contact…)
  document.querySelectorAll('.page-content > *').forEach((el, i) => {
    el.classList.add('reveal');
    el.dataset.delay = (i % 8) * 60;
  });

  const elements = document.querySelectorAll('.reveal');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry, i) => {
      if (entry.isIntersecting) {
        const delay = parseFloat(entry.target.dataset.delay || 0);
        setTimeout(() => entry.target.classList.add('visible'), delay);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  // Stagger cards in grids
  document.querySelectorAll('.feature-card').forEach((el, i) => {
    el.dataset.delay = (i % 3) * 80;
  });

  document.querySelectorAll('.testi-card').forEach((el, i) => {
    el.dataset.delay = i * 100;
  });

  document.querySelectorAll('.specs-col').forEach((el, i) => {
    el.dataset.delay = i * 80;
  });

  elements.forEach(el => observer.observe(el));
}


/* ── Particle canvas ────────────────────────────────── */
/* Décoratif. Audit perf 04-10 / F-23 : coupé sous 900 px (le canvas y est masqué en CSS)
   et en reduced-motion ; la boucle ne tourne que si le canvas est à l'écran et l'onglet visible ;
   28 particules au lieu de 55. */
function initCanvas() {
  const canvas = document.getElementById('heroCanvas');
  if (!canvas) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    canvas.remove();
    return;
  }

  const MIN_WIDTH = 900;
  const ctx = canvas.getContext('2d');

  let W = 0, H = 0, particles = [];
  let ready = false;     // taille + particules créées (seulement à partir de MIN_WIDTH)
  let inView = true;     // le canvas est (au moins en partie) à l'écran
  let rafId = 0;

  function canRun() {
    return ready && inView && !document.hidden && window.innerWidth >= MIN_WIDTH;
  }

  function resize() {
    W = canvas.width = window.innerWidth;
    H = canvas.height = window.innerHeight;
  }

  function createParticles() {
    particles = Array.from({ length: 28 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.8 + 0.4,
      vx: (Math.random() - 0.5) * 0.25,
      vy: -Math.random() * 0.4 - 0.1,
      alpha: Math.random() * 0.45 + 0.05,
      pulse: Math.random() * Math.PI * 2,
    }));
  }

  function drawFrame() {
    rafId = 0;
    if (!canRun()) return;           // la boucle s'arrête ; sync() la relancera

    ctx.clearRect(0, 0, W, H);

    particles.forEach(p => {
      p.pulse += 0.015;
      const a = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse));

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(201, 168, 76, ${a})`;
      ctx.fill();

      p.x += p.vx;
      p.y += p.vy;

      if (p.y < -10) p.y = H + 10;
      if (p.x < -10) p.x = W + 10;
      if (p.x > W + 10) p.x = -10;
    });

    rafId = requestAnimationFrame(drawFrame);
  }

  function sync() {
    if (canRun()) {
      if (!rafId) rafId = requestAnimationFrame(drawFrame);
    } else if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  }

  function setup() {
    if (window.innerWidth < MIN_WIDTH) { ready = false; return; }
    resize();
    createParticles();
    ready = true;
  }

  setup();

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      inView = entries[entries.length - 1].isIntersecting;
      sync();
    }).observe(canvas);
  }
  document.addEventListener('visibilitychange', sync);
  window.addEventListener('resize', () => { setup(); sync(); }, { passive: true });

  sync();
}


/* ── Hero parallax ──────────────────────────────────── */
function initParallax() {
  const heroContent = document.querySelector('.hero__content');
  const heroProduct = document.querySelector('.hero__product');
  if (!heroContent && !heroProduct) return;
  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const sy = window.scrollY;
        const vh = window.innerHeight;

        if (sy < vh && heroContent) {
          const progress = sy / vh;
          heroContent.style.transform = `translateY(${sy * 0.28}px)`;
          heroContent.style.opacity = String(Math.max(0, 1 - progress * 1.8));
        }

        if (sy < vh && heroProduct) {
          heroProduct.style.transform = `translateY(calc(-50% + ${sy * 0.12}px))`;
        }

        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
}


/* ── Smooth scroll for anchors ──────────────────────── */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const href = a.getAttribute('href');
      if (href === '#') return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      const offset = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    });
  });
}


/* ── Feature card hover shimmer ─────────────────────── */
function initCardShimmer() {
  document.querySelectorAll('.feature-card, .testi-card').forEach(card => {
    card.addEventListener('mousemove', e => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      card.style.setProperty('--mx', `${x}%`);
      card.style.setProperty('--my', `${y}%`);
    });
  });
}


/* ── Counter animation (stats) ──────────────────────── */
function initCounters() {
  const stats = document.querySelectorAll('.stat__number');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  stats.forEach(stat => observer.observe(stat));
}


/* ── Product 3D tilt on mouse ────────────────────────── */
function initProductTilt() {
  const product = document.querySelector('.product-3d');
  const heroSection = document.querySelector('.hero');
  if (!product || !heroSection) return;

  heroSection.addEventListener('mousemove', e => {
    const rect = heroSection.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rx = ((e.clientY - cy) / cy) * -6;
    const ry = ((e.clientX - cx) / cx) * 6;
    product.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  });

  heroSection.addEventListener('mouseleave', () => {
    product.style.transform = 'perspective(800px) rotateX(0) rotateY(0)';
  });
}


/* ── Custom gold cursor ─────────────────────────────── */
/* Audit perf 04-10 : plus de boucle rAF permanente. Les deux éléments sont déplacés par transform
   (pas de layout) et seulement tant que la bague n'a pas rattrapé le point ; souris immobile = 0 travail.
   Invisible tant que la souris n'a pas bougé. Les survols passent par une délégation d'événement.
   CONSTAT : en ligne, le curseur ne fonctionnait pas (ses éléments n'étaient pas positionnés : left/top
   sans effet, boucle rAF tournant pour rien). Le CSS est réparé (style.css), mais l'effet reste
   DÉSACTIVÉ ici pour ne rien ajouter de visible (décision F-23 « simplifier les effets »).
   Pour l'activer sur ordinateur : passer ENABLE_CUSTOM_CURSOR à true. */
const ENABLE_CUSTOM_CURSOR = false;

function initCursor() {
  if (!ENABLE_CUSTOM_CURSOR) return;
  const cursor = document.getElementById('cursor');
  const dot    = document.getElementById('cursorDot');
  const ring   = document.getElementById('cursorRing');
  if (!cursor || !dot || !ring) return;
  if (window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let dotX = 0, dotY = 0, ringX = 0, ringY = 0, raf = 0, shown = false;

  function lerp(a, b, t) { return a + (b - a) * t; }

  function paint() {
    dot.style.transform  = 'translate3d(' + dotX + 'px,' + dotY + 'px,0)';
    ring.style.transform = 'translate3d(' + ringX + 'px,' + ringY + 'px,0)';
  }

  function tick() {
    ringX = lerp(ringX, dotX, 0.12);
    ringY = lerp(ringY, dotY, 0.12);
    const moving = Math.abs(dotX - ringX) > 0.3 || Math.abs(dotY - ringY) > 0.3;
    if (!moving) { ringX = dotX; ringY = dotY; }
    paint();
    raf = moving ? requestAnimationFrame(tick) : 0;
  }

  document.addEventListener('mousemove', e => {
    dotX = e.clientX;
    dotY = e.clientY;
    if (!shown) {                       // 1er mouvement : pas de glissade depuis le coin
      shown = true;
      ringX = dotX;
      ringY = dotY;
      cursor.classList.add('cursor--on');
    }
    if (!raf) raf = requestAnimationFrame(tick);
  }, { passive: true });

  document.documentElement.addEventListener('mouseleave', () => cursor.classList.remove('cursor--on'));
  document.documentElement.addEventListener('mouseenter', () => { if (shown) cursor.classList.add('cursor--on'); });

  document.addEventListener('mouseover', e => {
    const hit = e.target.closest && e.target.closest('a, button, .feature-card, .testi-card, .gallery-item');
    cursor.classList.toggle('cursor--hover', !!hit);
  }, { passive: true });

  document.addEventListener('mousedown', () => cursor.classList.add('cursor--click'));
  document.addEventListener('mouseup',   () => cursor.classList.remove('cursor--click'));
}


/* ── Gold dividers ──────────────────────────────────── */
function initGoldDividers() {
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
    });
  }, { threshold: 0.4 });
  document.querySelectorAll('.gold-divider').forEach(d => obs.observe(d));
}


/* ── Magnetic buttons ───────────────────────────────── */
function initMagneticButtons() {
  document.querySelectorAll('.btn--gold, .btn-nav').forEach(btn => {
    btn.addEventListener('mousemove', e => {
      const r = btn.getBoundingClientRect();
      const dx = (e.clientX - r.left - r.width  / 2) * 0.22;
      const dy = (e.clientY - r.top  - r.height / 2) * 0.22;
      btn.style.transform = `translate(${dx}px, ${dy}px)`;
    });
    btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
  });
}


/* ── Section title underlines ───────────────────────── */
function initTitleUnderlines() {
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('.section-title-underline').forEach(el => obs.observe(el));
}


/* ── Water savings bars + counters ──────────────────── */
function initWaterSavings() {
  function animateCount(el, target, suffix, duration) {
    const start = performance.now();
    const isEuros = suffix === ' €';
    const frame = now => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      const val = Math.round(ease * target);
      el.textContent = isEuros
        ? val.toLocaleString('fr-FR') + ' €'
        : val.toLocaleString('fr-FR');
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  const milestones = document.getElementById('wsMilestones');
  if (!milestones) return;

  let countersStarted = false;
  const barsStarted = new Set();

  const barObs = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !barsStarted.has(entry.target)) {
        barsStarted.add(entry.target);
        entry.target.classList.add('animated');
        barObs.unobserve(entry.target);
      }
    });
  }, { threshold: 0.4 });

  document.querySelectorAll('.ws-bar-row').forEach(row => barObs.observe(row));

  const cardObs = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !countersStarted) {
        countersStarted = true;
        cardObs.unobserve(entry.target);

        document.querySelectorAll('.ws-card__liters[data-target]').forEach((el, i) => {
          setTimeout(() => animateCount(el, +el.dataset.target, '', 1800), i * 180);
        });
        document.querySelectorAll('.ws-card__euros[data-target]').forEach((el, i) => {
          setTimeout(() => animateCount(el, +el.dataset.target, ' €', 1800), 300 + i * 180);
        });
      }
    });
  }, { threshold: 0.3 });

  cardObs.observe(milestones);
}


/* ── Accordéon (FAQ + pages légales) ────────────────── */
function buildAccordion(acc, headingTag, stopTags) {
  acc.querySelectorAll(headingTag).forEach(h => {
    const item = document.createElement('div');
    item.className = 'faq-item';

    const btn = document.createElement('button');
    btn.className = 'faq-question';
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<span class="faq-question__text">' + h.innerHTML +
                    '</span><span class="faq-icon" aria-hidden="true"></span>';

    const answer = document.createElement('div');
    answer.className = 'faq-answer';
    const inner = document.createElement('div');
    inner.className = 'faq-answer__inner';

    // Récupère tout le contenu jusqu'au prochain titre de même niveau
    let sib = h.nextElementSibling;
    while (sib && stopTags.indexOf(sib.tagName) === -1) {
      const next = sib.nextElementSibling;
      inner.appendChild(sib);
      sib = next;
    }
    answer.appendChild(inner);

    h.replaceWith(item);
    item.appendChild(btn);
    item.appendChild(answer);

    btn.addEventListener('click', () => {
      const isOpen = item.classList.toggle('open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      answer.style.maxHeight = isOpen ? answer.scrollHeight + 'px' : null;
    });
  });
}

function initFaq() {
  // FAQ : chaque question (h3) devient un item ; les h2 restent des catégories
  document.querySelectorAll('.faq-accordion').forEach(acc => buildAccordion(acc, 'h3', ['H3', 'H2']));
  // Pages légales : chaque section (h2) devient un item dépliable
  document.querySelectorAll('.legal-accordion').forEach(acc => buildAccordion(acc, 'h2', ['H2']));
}


/* ── Init ───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initFaq();
  initReveal();
  initCanvas();
  initParallax();
  initSmoothScroll();
  initCardShimmer();
  initCounters();
  initProductTilt();
  initCursor();
  initGoldDividers();
  initMagneticButtons();
  initTitleUnderlines();
  initWaterSavings();

  handleNavScroll();
});
